import { createFileRoute } from "@tanstack/react-router";
import { CityLandingPage, buildCityLandingHead, type CityLandingConfig } from "@/components/CityLandingPage";
import heroImg from "@/assets/towns/castle-rock.jpg";

const CONFIG: CityLandingConfig = {
  slug: "castle-rock",
  name: "Castle Rock",
  region: "Douglas County",
  heroEyebrow: "Castle Rock Marketing Hub",
  heroTitle: { lead: "Castle Rock marketing for ", gold: "premium", trail: " Douglas County buyers" },
  heroDescription:
    "Castle Rock buyers research before they call — three site visits, two competitor reviews, one phone call. We build Castle Rock websites and SEO programs engineered to win that comparison.",
  intro:
    "Castle Rock is one of the most discerning markets on the Front Range. Buyers in The Meadows, Crystal Valley, Founders Village, and Castle Pines expect Cherry Creek-level polish but punish slow sites and shallow content. Our Castle Rock work is built for that exact comparison-shopping behavior.",
  neighborhoods: [
    "The Meadows",
    "Crystal Valley",
    "Founders Village",
    "Castle Pines",
    "Plum Creek",
    "Diamond Ridge",
    "Castlewood Ranch",
    "Downtown Castle Rock",
  ],
  marketSnapshot:
    "Castle Rock customer LTV runs 2–3× the Front Range average — but so do buyer expectations. A Castle Rock buyer who lands on your service page from a comparison search judges the whole brand on that single page. We design every page like it's the homepage.",
  highlights: [
    {
      title: "Premium polish without bloat",
      body: "Castle Rock expects Cherry Creek visual quality and won't tolerate slow loads. We ship sites that feel premium and load under 2 seconds — both, not either.",
    },
    {
      title: "Subdivision-aware messaging",
      body: "The Meadows tech professionals, Castle Pines luxury buyers, and Founders Village families read different signals. We write to your specific buyer subdivision.",
    },
    {
      title: "Built for multi-session comparison shopping",
      body: "Castle Rock buyers visit 2–4 times before converting. We build content depth, social proof, and trust architecture across every page — not just the homepage.",
    },
  ],
  services: [
    { to: "/castle-rock-seo", title: "Castle Rock SEO", body: "Map Pack rankings and content depth for The Meadows, Crystal Valley, Founders Village, and Castle Pines buyers." },
    { to: "/web-design-castle-rock-colorado", title: "Castle Rock Web Design", body: "Premium custom websites engineered for sub-2s loads and comparison-shopping behavior." },
    { to: "/colorado-springs-google-maps", title: "Google Maps optimization", body: "GBP optimization and review programs to land Castle Rock businesses in the 3-pack." },
    { to: "/colorado-springs-ppc", title: "Google Ads management", body: "Castle Rock PPC tuned to higher LTV — bidding strategies built for premium buyer behavior." },
  ],
  learnArticleSlugs: [
    "/learn/website-conversion",
    "/learn/mobile-website-mistakes",
    "/learn/local-seo-guide",
    "/learn/service-area-pages",
    "/learn/get-more-reviews",
    "/learn/agency-questions",
  ],
  learnIntro:
    "Castle Rock buyers are the most research-heavy on the Front Range. These six articles map cleanly to the questions Castle Rock owners ask us most — read in order for the fastest decision.",
  heroImage: { src: heroImg, alt: "The Castle Rock butte above downtown Castle Rock, Colorado", geo: { lat: 39.3722, lng: -104.8561, city: "Castle Rock, CO" } },
  closingCta:
    "We'll audit your Castle Rock site through the eyes of a Meadows or Castle Pines buyer — speed, polish, social proof, and conversion path — and tell you exactly what to fix first.",
};

export const Route = createFileRoute("/castle-rock")({
  head: () =>
    buildCityLandingHead(CONFIG, {
      metaTitle: "Castle Rock Marketing Agency | Premium Web Design & SEO",
      metaDescription:
        "Castle Rock marketing for The Meadows, Crystal Valley, Founders Village, and Castle Pines. Premium web design, SEO, and Google Ads built for Douglas County buyers.",
    }),
  component: () => <CityLandingPage config={CONFIG} />,
});
